import { useDeleteMasterMany } from '../../hooks/useDeleteMaster.js';
import type { MatrixPageState } from '../../hooks/useMatrixPageState.js';
import { confirmDeleteSelection } from '../DeleteMaster/DeleteSelectionConfirm.js';
import { EntityDrawer } from '../EntityDrawer/EntityDrawer.js';
import { InboxDrawer } from '../Inbox/InboxDrawer.js';
import { PushModal } from '../Push/PushModal.js';
import {
    adoptRequestsForSelection,
    deleteTargetsForSelection,
    summariseSelection,
} from '../Selection/selectionSummary.js';
import { SelectionBar } from '../Selection/SelectionBar.js';

/**
 * What `useMatrixPageState`'s selection, drawer, inbox and push drive: the selection bar (shown
 * only once something is ticked), the entity drawer, the inbox drawer and the push modal. Shared
 * by the Studios and Games pages — the one difference between them is whether the inbox is
 * scoped to a studio and whether closing the drawer needs to react (the Games page drops its
 * `?studio=` filter when the studio just open in the drawer got deleted).
 */
export const MatrixOverlays = ({
    state,
    environments,
    inboxStudio = null,
    onDeleted,
}: {
    state: MatrixPageState;
    /** Every environment column the grid behind this shows, for the selection bar's adopt menu. */
    environments: string[];
    /** The Games page's `?studio=` filter, so the inbox opened from here shows only that studio's records. */
    inboxStudio?: string | null;
    /** The Games page's own reaction to a delete from the drawer or the selection bar — dropping the filter if it named a deleted studio. */
    onDeleted?: (deleted: { type: string; key: string }[]) => void;
}) => {
    const { selection, drawer, setDrawer, inbox, setInbox, push, adoption } = state;
    const removeMany = useDeleteMasterMany();
    const summary = summariseSelection(selection.cells);

    return (
        <>
            {selection.count > 0 && (
                <SelectionBar
                    summary={summary}
                    environments={environments}
                    onPush={() =>
                        push.open({
                            cells: selection.cells.map(({ type, key, subtree }) => ({
                                type,
                                key,
                                ...(subtree ? { subtree } : {}),
                            })),
                        })
                    }
                    onAdopt={(environment) =>
                        adoption.adoptMany(adoptRequestsForSelection(environment, selection.cells))
                    }
                    onDelete={() => {
                        const targets = deleteTargetsForSelection(selection.cells);
                        confirmDeleteSelection(summary, () =>
                            removeMany.run(targets, () => {
                                selection.clear();
                                onDeleted?.(targets);
                            }),
                        );
                    }}
                    deleting={removeMany.isPending}
                    onClear={selection.clear}
                />
            )}

            {drawer && (
                <EntityDrawer
                    key={`${drawer.type}/${drawer.key}/${drawer.environment ?? ''}`}
                    type={drawer.type}
                    entityKey={drawer.key}
                    focusEnvironment={drawer.environment}
                    onClose={() => setDrawer(null)}
                    onPush={(environment, cell) => push.open({ cells: [cell], environments: [environment] })}
                    onDeleted={() => onDeleted?.([drawer])}
                />
            )}

            <InboxDrawer
                environment={inbox}
                studio={inboxStudio}
                onClose={() => setInbox(null)}
                onOpenEntity={(target) => {
                    setInbox(null);
                    setDrawer(target);
                }}
            />

            <PushModal request={push.request} onClose={push.close} />
        </>
    );
};
